import { useMemo } from "react";
import type { Field } from "~/types/api";
import { isFieldType, type FieldUnion } from "../utils/field-type-guards";

type UseMetadataChangesProps = {
  isEditMode: boolean;
  localFields: FieldUnion[];
  mutatedFieldIds: Set<number>;
  originalFields: Map<number, Field>;
  initialFieldsSnapshot: FieldUnion[];
};

export const useMetadataChanges = ({
  isEditMode,
  localFields,
  mutatedFieldIds,
  originalFields,
  initialFieldsSnapshot,
}: UseMetadataChangesProps) => {
  return useMemo(() => {
    if (!isEditMode) {
      return {
        toCreate: localFields,
        toUpdate: [] as Field[],
        toDelete: [] as number[],
        hasChanges: JSON.stringify(localFields) !== JSON.stringify(initialFieldsSnapshot),
      };
    }

    const toCreate: FieldUnion[] = [];
    const toUpdate: Field[] = [];
    const toDelete: number[] = [];

    const localFieldIds = new Set(localFields.filter(isFieldType).map((f) => f.id));

    localFields.forEach((field) => {
      if (!isFieldType(field)) {
        toCreate.push(field);
      } else if (mutatedFieldIds.has(field.id)) {
        toUpdate.push(field);
      }
    });

    originalFields.forEach((_, fieldId) => {
      if (!localFieldIds.has(fieldId)) {
        toDelete.push(fieldId);
      }
    });

    return {
      toCreate,
      toUpdate,
      toDelete,
      hasChanges: toCreate.length > 0 || toUpdate.length > 0 || toDelete.length > 0,
    };
  }, [isEditMode, localFields, mutatedFieldIds, originalFields, initialFieldsSnapshot]);
};
